/**
 * Hesab bərpası — şifrəni unutdum, şifrəni sıfırla, e-poçtu təsdiqlə.
 *
 * NİYƏ ÜÇÜ BİR YERDƏ
 * Üçü də eyni vəziyyətdə olan adama lazımdır: HƏLƏ GİRƏ BİLMİR. Eyni kart
 * dizaynını, eyni xəta lüğətini və eyni «girişə qayıt» keçidini paylaşırlar.
 * Ayrı fayllara bölsək, üç yerdə eyni şeyi saxlamalı olardıq.
 *
 * ⚠️ MÖVCUDLUQ SIZDIRILMIR
 * `forgot` addımında ünvan tapılmasa da eyni «göndərdik» mesajı göstərilir.
 * Server də eyni cavabı verir (bax: apps/panel-api/src/auth/reset.ts).
 * İnterfeys bunu pozmamalıdır — «belə istifadəçi yoxdur» yazsaq, bu ekran
 * hansı şirkətlərin bizdə hesabı olduğunu yoxlamaq üçün alətə çevrilər.
 */

import { useState, useEffect, type FormEvent } from 'react';
import { api, ApiError } from '../api';

/** Hansı addımdayıq. `token` yalnız sıfırlama və təsdiq üçün var. */
export type RecoverMode =
  | { kind: 'forgot' }
  | { kind: 'reset'; token: string }
  | { kind: 'verify'; token: string };

interface Props {
  mode: RecoverMode;
  /** Girişə qayıt — həm ləğv düyməsi, həm uğurdan sonrakı keçid. */
  onDone: () => void;
}

const MESSAGES: Record<string, string> = {
  too_many_attempts: 'Çox cəhd edildi. Bir neçə dəqiqədən sonra yenidən yoxlayın.',
  email_required: 'E-poçt ünvanını yazın.',
  token_and_password_required: 'Link natamamdır. Məktubdakı keçidi tam kopyalayın.',
  token_required: 'Link natamamdır. Məktubdakı keçidi tam kopyalayın.',
  invalid_token: 'Bu link etibarlı deyil. Yeni sıfırlama sorğusu göndərin.',
  expired: 'Linkin vaxtı keçib — o, yalnız 1 saat işləyir. Yeni sorğu göndərin.',
  used: 'Bu link artıq işlədilib. Şifrəniz dəyişibsə, yeni şifrə ilə daxil olun.',
  email_changed: 'Hesabın e-poçtu dəyişdiyi üçün bu link işləmir. Yeni sorğu göndərin.',
  weak_password: 'Şifrə ən azı 10 simvol olmalıdır.',
  already: 'Bu ünvan artıq təsdiqlənib. Sadəcə daxil olun.',
};

function messageFor(err: unknown): string {
  const code = err instanceof ApiError ? err.code : 'unknown';
  return MESSAGES[code] ?? 'Əməliyyat alınmadı. Bir azdan yenidən yoxlayın.';
}

export function Recover({ mode, onDone }: Props): React.ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // Təsdiq linkində istifadəçidən heç nə soruşulmur — açılan kimi işləyir.
  useEffect(() => {
    if (mode.kind !== 'verify') return;
    let cancelled = false;
    setBusy(true);
    api
      .verifyEmail(mode.token)
      .then(({ email: e }) => {
        if (!cancelled) setDone(`${e} ünvanı təsdiqləndi. İndi daxil ola bilərsiniz.`);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(messageFor(err));
      })
      .finally(() => {
        if (!cancelled) setBusy(false);
      });
    return () => {
      cancelled = true;
    };
  }, [mode]);

  async function submitForgot(e: FormEvent): Promise<void> {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.forgot(email);
      // ⚠️ Ünvan tapılmasa da bura düşürük — server də uğur qaytarır.
      setDone(
        `Əgər ${email} ünvanı bizdə qeydiyyatdadırsa, sıfırlama keçidi göndərildi. ` +
          'Poçtunuzu yoxlayın — keçid 1 saat işləyir.',
      );
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  async function submitReset(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (mode.kind !== 'reset') return;
    if (password !== repeat) {
      setError('Şifrələr eyni deyil.');
      return;
    }
    if (password.length < 10) {
      setError('Şifrə ən azı 10 simvol olmalıdır.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { email: e2 } = await api.resetPassword(mode.token, password);
      setDone(`${e2} üçün şifrə dəyişdirildi. Bütün cihazlardakı sessiyalar bağlandı.`);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  }

  const title =
    mode.kind === 'forgot'
      ? 'Şifrəni sıfırla'
      : mode.kind === 'reset'
        ? 'Yeni şifrə təyin edin'
        : 'E-poçt təsdiqi';

  return (
    <div className="login">
      <div className="login-card">
        <div className="login-brand">
          <span className="login-mark">M</span>
          <span className="login-name">Mailora</span>
        </div>
        <p className="login-lead">{title}</p>

        {done ? (
          <>
            <p className="login-ok" role="status">
              {done}
            </p>
            <button type="button" className="btn-primary" onClick={onDone}>
              Girişə qayıt
            </button>
          </>
        ) : mode.kind === 'verify' ? (
          <>
            {busy && <p className="login-hint">Təsdiqlənir…</p>}
            {error && (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}
            {!busy && (
              <button type="button" className="btn-primary" onClick={onDone}>
                Girişə qayıt
              </button>
            )}
          </>
        ) : mode.kind === 'forgot' ? (
          <form onSubmit={submitForgot}>
            <p className="login-hint">
              Hesabınızın e-poçt ünvanını yazın. Şifrəni yeniləmək üçün ora birdəfəlik keçid
              göndərəcəyik.
            </p>
            <label className="field">
              <span>E-poçt ünvanı</span>
              <input
                type="email"
                autoComplete="username"
                required
                autoFocus
                value={email}
                onChange={(ev) => setEmail(ev.target.value)}
                placeholder="ad@sirketiniz.az"
              />
            </label>
            {error && (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Göndərilir…' : 'Keçid göndər'}
            </button>
            <p className="login-alt">
              <button type="button" className="linkish" onClick={onDone}>
                Girişə qayıt
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={submitReset}>
            <p className="login-hint">
              Yeni şifrə ən azı 10 simvol olmalıdır. Dəyişdikdən sonra bütün cihazlardakı
              sessiyalar bağlanacaq.
            </p>
            <label className="field">
              <span>Yeni şifrə</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                autoFocus
                value={password}
                onChange={(ev) => setPassword(ev.target.value)}
              />
            </label>
            <label className="field">
              <span>Yeni şifrə (təkrar)</span>
              <input
                type="password"
                autoComplete="new-password"
                required
                value={repeat}
                onChange={(ev) => setRepeat(ev.target.value)}
              />
            </label>
            {error && (
              <p className="login-error" role="alert">
                {error}
              </p>
            )}
            <button type="submit" className="btn-primary" disabled={busy}>
              {busy ? 'Dəyişdirilir…' : 'Şifrəni dəyiş'}
            </button>
            <p className="login-alt">
              <button type="button" className="linkish" onClick={onDone}>
                Girişə qayıt
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
